import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import JSZip from "jszip";
import {
  ArrowBack,
  FolderZip,
  CloudUpload,
  Smartphone,
  Android,
  Check,
  Edit,
  Close,
  PhotoCamera,
  ZoomIn,
  ZoomOut,
  RestartAlt,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  AutoFixHigh,
  DeleteSweep,
  FitScreen
} from "@mui/icons-material";

// Goldie Transparent Checkerboard Pattern
export const TRANSPARENT = "transparent";
export const CHECKERBOARD = "repeating-conic-gradient(#c8c8c8 0 25%, #f4f4f4 0 50%) 0 0/12px 12px";

// Goldie Exact Curated Gradients (23 options)
export const GRADIENTS = [
  { name: "Arctic", css: "linear-gradient(160deg, #E8F1FF 0%, #F7FAFF 55%, #FFFFFF 100%)" },
  { name: "Peach", css: "linear-gradient(160deg, #FFE8D6 0%, #FFF7F0 55%, #FFFFFF 100%)" },
  { name: "Mint", css: "linear-gradient(160deg, #D9F9EF 0%, #F2FDF9 55%, #FFFFFF 100%)" },
  { name: "Lavender", css: "linear-gradient(160deg, #E9E4FF 0%, #F7F5FF 55%, #FFFFFF 100%)" },
  { name: "Sand", css: "linear-gradient(160deg, #F7ECDD 0%, #FCF7F0 55%, #FFFFFF 100%)" },
  { name: "Blush", css: "linear-gradient(160deg, #FFE0E9 0%, #FFF5F8 55%, #FFFFFF 100%)" },
  { name: "Lemon", css: "linear-gradient(160deg, #FFF3C4 0%, #FFFBEB 55%, #FFFFFF 100%)" },
  { name: "Silver", css: "linear-gradient(160deg, #E2E8F0 0%, #F1F5F9 55%, #FFFFFF 100%)" },
  { name: "Ocean", css: "linear-gradient(160deg, #0EA5E9 0%, #2563EB 100%)" },
  { name: "Ember", css: "linear-gradient(160deg, #F97316 0%, #DC2626 100%)" },
  { name: "Forest", css: "linear-gradient(160deg, #059669 0%, #065F46 100%)" },
  { name: "Midnight", css: "linear-gradient(160deg, #1E293B 0%, #0F172A 100%)" },
  { name: "Grape", css: "linear-gradient(160deg, #8B5CF6 0%, #4C1D95 100%)" },
  { name: "Berry", css: "linear-gradient(160deg, #EC4899 0%, #831843 100%)" },
  { name: "Aurora", css: "linear-gradient(160deg, #0D9488 0%, #1E3A8A 100%)" },
  { name: "Graphite", css: "linear-gradient(160deg, #3F3F46 0%, #18181B 100%)" },
  { name: "Sky", css: "linear-gradient(160deg, #BAE6FD 0%, #E0F2FE 55%, #FFFFFF 100%)" },
  { name: "Rose", css: "linear-gradient(160deg, #FDA4AF 0%, #FFE4E6 60%, #FFFFFF 100%)" },
  { name: "Sunset", css: "linear-gradient(160deg, #F59E0B 0%, #EF4444 55%, #7C3AED 100%)" },
  { name: "Coral", css: "linear-gradient(160deg, #FB7185 0%, #F97316 100%)" },
  { name: "Teal", css: "linear-gradient(160deg, #2DD4BF 0%, #0F766E 100%)" },
  { name: "Cobalt", css: "linear-gradient(160deg, #3B82F6 0%, #1E1B4B 100%)" },
  { name: "Obsidian Gold", css: "linear-gradient(160deg, #131418 0%, #221E17 55%, #0E0F12 100%)" }
];

export const SOLIDS = [
  { name: "White", css: "#FFFFFF" },
  { name: "Cream", css: "#FAF6EE" },
  { name: "Ash", css: "#E2E8F0" },
  { name: "Slate", css: "#64748B" },
  { name: "Charcoal", css: "#27272A" },
  { name: "Black", css: "#000000" },
  { name: "Navy", css: "#1E3A8A" },
  { name: "Blue", css: "#2563EB" },
  { name: "Cyan", css: "#06B6D4" },
  { name: "Emerald", css: "#10B981" },
  { name: "Lime", css: "#84CC16" },
  { name: "Amber", css: "#F59E0B" },
  { name: "Tangerine", css: "#EA580C" },
  { name: "Crimson", css: "#DC2626" },
  { name: "Fuchsia", css: "#D946EF" },
  { name: "Violet", css: "#7C3AED" }
];

// Goldie Exact Frames & Zero-Leak Screen Cutout Geometry (iPhone & Android)
export const FRAME_META = {
  "17-pro-silver": {
    label: "Titanium Silver",
    tint: "#D8D9DD",
    device: "iphone",
    image: "/frames/17-pro-silver.png",
    // Seamless inner fit geometry: 0 outside corner leak, 0 gap
    geom: { width: 606, height: 1252, screen: { x: 22, y: 19, width: 562, height: 1214 }, radius: 72 }
  },
  "17-pro-blue": {
    label: "Deep Blue",
    tint: "#2B3A5C",
    device: "iphone",
    image: "/frames/17-pro-blue.png",
    geom: { width: 606, height: 1252, screen: { x: 22, y: 19, width: 562, height: 1214 }, radius: 72 }
  },
  "17-pro-orange": {
    label: "Cosmic Orange",
    tint: "#E0662F",
    device: "iphone",
    image: "/frames/17-pro-orange.png",
    geom: { width: 606, height: 1252, screen: { x: 22, y: 19, width: 562, height: 1214 }, radius: 72 }
  },
  "android-pixel": {
    label: "Obsidian",
    tint: "#1C1D22",
    device: "android",
    image: "/frames/pixel-10-pro.webp",
    // Seamless inner fit geometry: 0 outside corner leak, 0 gap
    geom: { width: 1410, height: 2968, screen: { x: 56, y: 57, width: 1286, height: 2862 }, radius: 165 }
  }
};

// Font Options with Specimen
export const FONT_OPTIONS = [
  { css: '-apple-system, "SF Pro Display", system-ui, sans-serif', label: "System (SF Pro)" },
  { css: '"Inter", sans-serif', label: "Inter" },
  { css: '"Manrope", sans-serif', label: "Manrope" },
  { css: '"Outfit", sans-serif', label: "Outfit" },
  { css: '"Plus Jakarta Sans", sans-serif', label: "Plus Jakarta Sans" },
  { css: '"Syne", sans-serif', label: "Syne" },
  { css: '"Playfair Display", serif', label: "Playfair Display" },
  { css: '"Cairo", sans-serif', label: "Cairo" }
];

// Layout Geometry Specifications (Balanced Goldie Proportions)
export const LAYOUT_DEFS = {
  classic: {
    key: "classic",
    label: "Classic",
    desc: "Centered headline above a centered floating frame",
    copyPosition: "top",
    copyAlign: "center",
    deviceWidthPct: 76,
    deviceOffsetTop: 0,
    deviceOffsetLeft: 0,
    deviceRotate: 0,
    bleedBottom: false,
    fitBelowCopy: true
  },
  hero: {
    key: "hero",
    label: "Hero",
    desc: "Headline on top, large frame running off bottom edge",
    copyPosition: "top",
    copyAlign: "center",
    deviceWidthPct: 84,
    deviceOffsetTop: 0,
    deviceOffsetLeft: 0,
    deviceRotate: 0,
    bleedBottom: true
  },
  "copy-below": {
    key: "copy-below",
    label: "Copy Below",
    desc: "Device at top, headline & subtitle cleanly below",
    copyPosition: "bottom",
    copyAlign: "center",
    deviceWidthPct: 68,
    deviceOffsetTop: 0,
    deviceOffsetLeft: 0,
    deviceRotate: 0,
    bleedBottom: false
  },
  offset: {
    key: "offset",
    label: "Offset",
    desc: "Left-aligned copy, device shifted to the bottom right",
    copyPosition: "top",
    copyAlign: "left",
    deviceWidthPct: 84,
    deviceOffsetTop: 0,
    deviceOffsetLeft: 18,
    deviceRotate: 0,
    bleedBottom: true
  },
  tilt: {
    key: "tilt",
    label: "Tilt Left",
    desc: "Headline on top, frame tilted -8° running off bottom",
    copyPosition: "top",
    copyAlign: "center",
    deviceWidthPct: 82,
    deviceOffsetTop: 0,
    deviceOffsetLeft: 0,
    deviceRotate: -8,
    bleedBottom: true
  },
  "tilt-right": {
    key: "tilt-right",
    label: "Tilt Right",
    desc: "Left-aligned copy, frame tilted +10° into bottom right",
    copyPosition: "top",
    copyAlign: "left",
    deviceWidthPct: 82,
    deviceOffsetTop: 0,
    deviceOffsetLeft: 12,
    deviceRotate: 10,
    bleedBottom: true
  },
  minimal: {
    key: "minimal",
    label: "Minimal",
    desc: "No copy, pure sleek centered device with soft shadow",
    copyPosition: "none",
    copyAlign: "center",
    deviceWidthPct: 80,
    deviceOffsetTop: 0,
    deviceOffsetLeft: 0,
    deviceRotate: 0,
    bleedBottom: false
  }
};

// Templates: Sequences of Layouts across the Strip (Goldie Exact Templates)
export const TEMPLATES = [
  {
    key: "editorial",
    label: "Editorial",
    desc: "Hero opener, then offset, copy-below, dynamic tilt & classic",
    sequence: ["hero", "offset", "copy-below", "tilt", "classic"]
  },
  {
    key: "classic",
    label: "Classic Uniform",
    desc: "Centered headline above a centered floating frame",
    sequence: ["classic", "classic", "classic", "classic", "classic"]
  },
  {
    key: "hero",
    label: "Hero Bleed",
    desc: "Bold headlines with large frames running off the bottom edge",
    sequence: ["hero", "hero", "hero", "hero", "hero"]
  },
  {
    key: "copy-below",
    label: "Copy Below",
    desc: "Frame hanging at top with headline & subtitle at the bottom",
    sequence: ["copy-below", "copy-below", "copy-below", "copy-below", "copy-below"]
  },
  {
    key: "showcase",
    label: "Showcase",
    desc: "Hero first, then tilted and offset frames, ending on minimal",
    sequence: ["hero", "tilt", "offset", "tilt-right", "minimal"]
  },
  {
    key: "magazine",
    label: "Magazine",
    desc: "Left-aligned copy and copy-below alternating with hero",
    sequence: ["offset", "copy-below", "tilt-right", "hero", "minimal"]
  },
  {
    key: "tilt",
    label: "Dynamic Tilt",
    desc: "Kinetic ribbon of alternating -8° and +10° angled frames",
    sequence: ["tilt", "tilt-right", "tilt", "tilt-right", "tilt"]
  },
  {
    key: "minimal",
    label: "Screen Only",
    desc: "Frameless pure devices, no text, minimal Apple store look",
    sequence: ["minimal", "minimal", "minimal", "minimal", "minimal"]
  }
];

// Helper: Calculate luminance to pick contrasting text colors
function isLightColor(color) {
  if (!color || color === TRANSPARENT) return false;
  const hexes = color.match(/#[0-9a-fA-F]{6}/g);
  if (!hexes || hexes.length === 0) return false;
  const hex = hexes[0].replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 155;
}

// Goldie Exact Template Strip Glyph (Mini SVG Strip of 5 Tiles)
const TemplateGlyphs = ({ sequence }) => {
  const TILE_W = 44;
  const TILE_H = 80;
  const GAP = 4;
  const totalW = sequence.length * TILE_W + (sequence.length - 1) * GAP;

  return (
    <svg viewBox={`0 0 ${totalW} ${TILE_H}`} className="template-strip-svg" aria-hidden>
      {sequence.map((layoutKey, idx) => {
        const x = idx * (TILE_W + GAP);
        const isOffset = layoutKey === "offset";
        const isCopyBelow = layoutKey === "copy-below";
        const isTilt = layoutKey === "tilt";
        const isTiltRight = layoutKey === "tilt-right";
        const isMinimal = layoutKey === "minimal";
        const isHero = layoutKey === "hero";

        return (
          <g key={idx} transform={`translate(${x}, 0)`}>
            {/* Tile Background */}
            <rect width={TILE_W} height={TILE_H} rx="4" className="glyph-tile-bg" />

            {/* Headline and Subhead lines */}
            {!isMinimal && !isCopyBelow && (
              <>
                <rect
                  x={isOffset || isTiltRight ? 4 : 6}
                  y="6"
                  width={isOffset || isTiltRight ? 24 : 32}
                  height="3.5"
                  rx="1.75"
                  className="glyph-text-bar"
                />
                <rect
                  x={isOffset || isTiltRight ? 4 : 10}
                  y="12"
                  width={isOffset || isTiltRight ? 16 : 24}
                  height="2.5"
                  rx="1.25"
                  className="glyph-text-sub"
                />
              </>
            )}

            {isCopyBelow && (
              <>
                <rect x="6" y="62" width="32" height="3.5" rx="1.75" className="glyph-text-bar" />
                <rect x="10" y="68" width="24" height="2.5" rx="1.25" className="glyph-text-sub" />
              </>
            )}

            {/* Device Shape */}
            <g clipPath={`url(#tile-clip-${idx})`}>
              <clipPath id={`tile-clip-${idx}`}>
                <rect width={TILE_W} height={TILE_H} rx="4" />
              </clipPath>

              {isHero && (
                <rect x="5" y="20" width="34" height="70" rx="4" className="glyph-device" />
              )}
              {isOffset && (
                <rect x="12" y="20" width="32" height="70" rx="4" className="glyph-device" />
              )}
              {isCopyBelow && (
                <rect x="8" y="10" width="28" height="48" rx="4" className="glyph-device" />
              )}
              {isTilt && (
                <rect
                  x="6"
                  y="20"
                  width="32"
                  height="68"
                  rx="4"
                  transform="rotate(-8 22 54)"
                  className="glyph-device"
                />
              )}
              {isTiltRight && (
                <rect
                  x="8"
                  y="20"
                  width="32"
                  height="68"
                  rx="4"
                  transform="rotate(10 24 54)"
                  className="glyph-device"
                />
              )}
              {isMinimal && (
                <rect x="6" y="10" width="32" height="60" rx="4" className="glyph-device" />
              )}
              {layoutKey === "classic" && (
                <rect x="6" y="19" width="32" height="56" rx="4" className="glyph-device" />
              )}
            </g>
          </g>
        );
      })}
    </svg>
  );
};

// Canvas Multi-Line Text Wrapping Helper
function wrapCanvasText(ctx, text, x, y, maxWidth, lineHeight) {
  if (!text) return 0;
  const words = text.split(" ");
  let line = "";
  const lines = [];

  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + " ";
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && n > 0) {
      lines.push(line.trim());
      line = words[n] + " ";
    } else {
      line = testLine;
    }
  }
  lines.push(line.trim());

  lines.forEach((l, i) => {
    ctx.fillText(l, x, y + i * lineHeight);
  });
  return lines.length * lineHeight;
}

// High-Converting Dummy Headlines & Subheads (Automatically added)
const DUMMY_HEADLINES = [
  "Discover Fast & Seamless",
  "Connect With Real People",
  "Private & End-to-End Encrypted",
  "Unlock Exclusive VIP Perks",
  "Experience Luxury & Simplicity",
  "Real-Time Live Radar"
];

const DUMMY_SUBHEADS = [
  "Verified profiles, instant matching & live distance.",
  "Skip awkward icebreakers with direct invites.",
  "24-Hour disappearing messages & verified user safety.",
  "City Passport, Incognito Ghost Mode and priority rank.",
  "Built with precision and high-performance design.",
  "Browse hotspots, events and nearby friends instantly."
];

const SnapStudio = () => {
  // 1. Initial State: Empty screenshots array (NO DEMO PRELOADED)
  const [slides, setSlides] = useState([]);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  // Global Design State (Remember user selection in localStorage)
  const [deviceType, setDeviceType] = useState(() => {
    return localStorage.getItem("snapstudio_device") || "android";
  });
  const [screenOnly, setScreenOnly] = useState(false);
  const [frameKey, setFrameKey] = useState(() => {
    return localStorage.getItem("snapstudio_frame") || "android-pixel";
  });
  const [backgroundTab, setBackgroundTab] = useState("gradients");
  const [background, setBackground] = useState(GRADIENTS[8].css); // Ocean default
  const [template, setTemplate] = useState("editorial");
  const [fontFamily, setFontFamily] = useState('-apple-system, "SF Pro Display", system-ui, sans-serif');
  const [textAlign, setTextAlign] = useState(() => {
    return localStorage.getItem("snapstudio_align") || "center";
  });

  // Custom Color Popover State (Pencil / Conic Gradient tool)
  const [customColorOpen, setCustomColorOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("#2563EB");
  const [customTo, setCustomTo] = useState("#0EA5E9");
  const [customAngle, setCustomAngle] = useState(160);

  // Stage Zoom State & Gesture Controls (Pinch, Wheel, Pan)
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const stageRef = useRef(null);
  const viewportRef = useRef(null);

  // Export State
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState("");

  const fileInputRef = useRef(null);

  // Selected template object
  const activeTemplate = TEMPLATES.find((t) => t.key === template) || TEMPLATES[0];

  // Selected frame metadata
  const activeFrame = FRAME_META[frameKey] || FRAME_META["android-pixel"];

  // Helper to get layout for a slide index
  const getSlideLayoutKey = useCallback(
    (slide, index) => {
      if (slide.layoutOverride) return slide.layoutOverride;
      const seq = activeTemplate.sequence;
      return seq[index % seq.length] || "classic";
    },
    [activeTemplate]
  );

  // Handle device change and persist
  const handleDeviceChange = (newDevice) => {
    setDeviceType(newDevice);
    localStorage.setItem("snapstudio_device", newDevice);
    if (newDevice === "iphone") {
      setFrameKey("17-pro-blue");
      localStorage.setItem("snapstudio_frame", "17-pro-blue");
    } else {
      setFrameKey("android-pixel");
      localStorage.setItem("snapstudio_frame", "android-pixel");
    }
  };

  const handleFrameChange = (key) => {
    setFrameKey(key);
    localStorage.setItem("snapstudio_frame", key);
  };

  const handleAlignmentChange = (newAlign) => {
    setTextAlign(newAlign);
    localStorage.setItem("snapstudio_align", newAlign);
  };

  // Update template and automatically refresh dummy copy if slides are empty of text
  const handleTemplateChange = (newTemplateKey) => {
    setTemplate(newTemplateKey);
    setSlides((prev) =>
      prev.map((s, idx) => ({
        ...s,
        headline: s.headline ? s.headline : DUMMY_HEADLINES[idx % DUMMY_HEADLINES.length],
        subhead: s.subhead ? s.subhead : DUMMY_SUBHEADS[idx % DUMMY_SUBHEADS.length]
      }))
    );
  };

  // Dynamic SEO Meta Tags & Schema.org Structured Data for Google Ranking
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Free App Store & Google Play Store Screenshot Generator | SnapStudio";

    const metaConfigs = [
      {
        name: "description",
        content:
          "Create beautiful, high-converting App Store and Google Play screenshots online for free. Authentic iPhone 17 Pro and Android Google Pixel frames, curated gradients, stylish typography, and instant batch ZIP download."
      },
      {
        name: "keywords",
        content:
          "free google playstore screenshot, free app store screenshot generator, app store screenshot maker, play store screenshot maker, app mockup generator, app store mockup tool, iphone 17 pro screenshot frame, android pixel screenshot frame, free mobile screenshot builder, export app screenshots"
      },
      {
        property: "og:title",
        content: "SnapStudio - Free App Store & Google Play Screenshot Generator"
      },
      {
        property: "og:description",
        content:
          "Generate professional App Store and Google Play screenshots in seconds. Free device frames, custom text, curated gradients, and instant export."
      },
      {
        property: "og:url",
        content: "https://appsy-app.netlify.app/tools/snapstudio"
      }
    ];

    const originalMetas = [];
    metaConfigs.forEach(({ name, property, content }) => {
      let metaEl = name
        ? document.querySelector(`meta[name="${name}"]`)
        : document.querySelector(`meta[property="${property}"]`);

      if (metaEl) {
        originalMetas.push({ el: metaEl, prev: metaEl.getAttribute("content") });
        metaEl.setAttribute("content", content);
      } else {
        const newEl = document.createElement("meta");
        if (name) newEl.setAttribute("name", name);
        if (property) newEl.setAttribute("property", property);
        newEl.setAttribute("content", content);
        document.head.appendChild(newEl);
        originalMetas.push({ el: newEl, isNew: true });
      }
    });

    // JSON-LD Structured Data for Google Rich Snippets
    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "SnapStudio - Free App Store & Google Play Screenshot Generator",
      "url": "https://appsy-app.netlify.app/tools/snapstudio",
      "applicationCategory": "DesignApplication",
      "operatingSystem": "All",
      "description":
        "Free online tool to generate professional Google Play Store and Apple App Store screenshots with authentic device frames, gradient backgrounds, and instant ZIP export.",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "keywords":
        "free google playstore screenshot, free app store screenshot generator, play store screenshot maker, app store mockup generator, mobile mockup tool"
    });
    document.head.appendChild(schemaScript);

    return () => {
      document.title = prevTitle;
      originalMetas.forEach(({ el, prev, isNew }) => {
        if (isNew) {
          el.remove();
        } else if (prev) {
          el.setAttribute("content", prev);
        }
      });
      if (schemaScript.parentNode) {
        schemaScript.remove();
      }
    };
  }, []);

  // 1. Gesture Zoom: Native Wheel + Trackpad Pinch
  useEffect(() => {
    const stageEl = stageRef.current;
    if (!stageEl) return;

    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) {
        e.preventDefault();
        const factor = Math.exp(-e.deltaY * 0.01);
        setZoomLevel((prev) => Math.min(2.5, Math.max(0.35, Number((prev * factor).toFixed(3)))));
      }
    };

    stageEl.addEventListener("wheel", handleWheel, { passive: false });
    return () => stageEl.removeEventListener("wheel", handleWheel);
  }, []);

  // 2. Gesture Zoom: Touchscreen Pinch Gesture
  const touchDistRef = useRef(null);
  const touchZoomStartRef = useRef(1);

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      touchDistRef.current = dist;
      touchZoomStartRef.current = zoomLevel;
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && touchDistRef.current) {
      e.preventDefault();
      const currentDist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const scale = currentDist / touchDistRef.current;
      setZoomLevel(Math.min(2.5, Math.max(0.35, Number((touchZoomStartRef.current * scale).toFixed(3)))));
    }
  };

  const handleTouchEnd = () => {
    touchDistRef.current = null;
  };

  // 3. Pan dragging with mouse
  const handleMouseDownStage = (e) => {
    if (e.target.closest(".strip-tile") || e.target.closest("button") || e.target.closest("input") || e.target.closest("textarea")) {
      return;
    }
    setIsPanning(true);
    setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
  };

  const handleMouseMoveStage = (e) => {
    if (!isPanning) return;
    setPanOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUpStage = () => {
    setIsPanning(false);
  };

  // Reset zoom & pan
  const handleResetView = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  // Auto-fit zoom to view all screens
  const handleFitToScreen = () => {
    if (slides.length === 0) {
      setZoomLevel(1);
      return;
    }
    const stageWidth = stageRef.current ? stageRef.current.clientWidth - 100 : 900;
    const requiredWidth = slides.length * 315 + (slides.length - 1) * 20;
    const optimalZoom = Math.min(1.1, Math.max(0.4, stageWidth / requiredWidth));
    setZoomLevel(Number(optimalZoom.toFixed(2)));
    setPanOffset({ x: 0, y: 0 });
  };

  // Handle image upload (automatically populates dummy text with template style!)
  const handleFilesUpload = (files) => {
    if (!files || files.length === 0) return;

    const newSlides = [];
    let processed = 0;

    Array.from(files).forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const headlineText = DUMMY_HEADLINES[(slides.length + index) % DUMMY_HEADLINES.length];
        const subheadText = DUMMY_SUBHEADS[(slides.length + index) % DUMMY_SUBHEADS.length];

        newSlides.push({
          id: Date.now() + index + Math.random(),
          headline: headlineText,
          subhead: subheadText,
          image: e.target.result,
          layoutOverride: undefined
        });

        processed++;
        if (processed === files.length) {
          setSlides((prev) => [...prev, ...newSlides]);
          if (slides.length === 0) setActiveSlideIndex(0);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Drag and drop handlers
  const [isDragging, setIsDragging] = useState(false);
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesUpload(e.dataTransfer.files);
    }
  };

  // Explore with Sample Styles (if user clicks exploring)
  const handleLoadSampleStyles = () => {
    const demoSvg1 = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1080" height="2340" viewBox="0 0 1080 2340"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="%231E293B"/><stop offset="100%" stop-color="%230F172A"/></linearGradient></defs><rect width="1080" height="2340" fill="url(%23g)"/><circle cx="540" cy="900" r="260" fill="%232563EB" opacity="0.3"/><rect x="100" y="1300" width="880" height="140" rx="30" fill="%232563EB"/><text x="540" y="1390" fill="%23FFFFFF" font-family="system-ui" font-weight="bold" font-size="52" text-anchor="middle">Sample Screen 1</text></svg>`;
    const demoSvg2 = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1080" height="2340" viewBox="0 0 1080 2340"><defs><linearGradient id="g2" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="%23131418"/><stop offset="100%" stop-color="%230E0F12"/></linearGradient></defs><rect width="1080" height="2340" fill="url(%23g2)"/><circle cx="540" cy="900" r="260" fill="%23D4B56A" opacity="0.3"/><rect x="100" y="1300" width="880" height="140" rx="30" fill="%23D4B56A"/><text x="540" y="1390" fill="%230E0F12" font-family="system-ui" font-weight="bold" font-size="52" text-anchor="middle">Sample Screen 2</text></svg>`;
    const demoSvg3 = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1080" height="2340" viewBox="0 0 1080 2340"><defs><linearGradient id="g3" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="%230F172A"/><stop offset="100%" stop-color="%23020617"/></linearGradient></defs><rect width="1080" height="2340" fill="url(%23g3)"/><circle cx="540" cy="900" r="260" fill="%2310B981" opacity="0.3"/><rect x="100" y="1300" width="880" height="140" rx="30" fill="%2310B981"/><text x="540" y="1390" fill="%23FFFFFF" font-family="system-ui" font-weight="bold" font-size="52" text-anchor="middle">Sample Screen 3</text></svg>`;
    setSlides([
      { id: 1, headline: DUMMY_HEADLINES[0], subhead: DUMMY_SUBHEADS[0], image: demoSvg1 },
      { id: 2, headline: DUMMY_HEADLINES[1], subhead: DUMMY_SUBHEADS[1], image: demoSvg2 },
      { id: 3, headline: DUMMY_HEADLINES[2], subhead: DUMMY_SUBHEADS[2], image: demoSvg3 }
    ]);
    setActiveSlideIndex(0);
  };

  // Populate or Clear dummy texts
  const handlePopulateDummyText = () => {
    setSlides(
      slides.map((s, i) => ({
        ...s,
        headline: DUMMY_HEADLINES[i % DUMMY_HEADLINES.length],
        subhead: DUMMY_SUBHEADS[i % DUMMY_SUBHEADS.length]
      }))
    );
  };

  const handleClearAllText = () => {
    setSlides(slides.map((s) => ({ ...s, headline: "", subhead: "" })));
  };

  // Custom Color Application
  const applyCustomColor = () => {
    const customCss =
      customFrom.toLowerCase() === customTo.toLowerCase()
        ? customFrom
        : `linear-gradient(${customAngle}deg, ${customFrom} 0%, ${customTo} 100%)`;
    setBackground(customCss);
    setCustomColorOpen(false);
  };

  // High-Resolution Export with True Bezel & Exact Layout Geometry
  const renderTileToCanvas = (slide, index, width = 1290, height = 2796) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      // Draw background
      if (background !== TRANSPARENT) {
        if (background.startsWith("linear-gradient")) {
          const bgG = ctx.createLinearGradient(0, 0, width * 0.4, height);
          const hexes = background.match(/#[0-9a-fA-F]{6}/g) || ["#0EA5E9", "#2563EB"];
          bgG.addColorStop(0, hexes[0]);
          bgG.addColorStop(1, hexes[hexes.length - 1]);
          ctx.fillStyle = bgG;
        } else {
          ctx.fillStyle = background;
        }
        ctx.fillRect(0, 0, width, height);
      }

      const layoutKey = getSlideLayoutKey(slide, index);
      const layoutSpec = LAYOUT_DEFS[layoutKey] || LAYOUT_DEFS.classic;
      const isTextNone = layoutSpec.copyPosition === "none";
      const isTextBottom = layoutSpec.copyPosition === "bottom";
      const slideAlign = slide.align || textAlign || layoutSpec.copyAlign || "center";

      // 1. Draw Headline & Subhead (Balanced size and spacing)
      if (!isTextNone) {
        const maxTextW = width * 0.86;
        const headlineLineHeight = Math.round(width * 0.076);
        const subheadLineHeight = Math.round(width * 0.040);
        const textX =
          slideAlign === "left"
            ? width * 0.08
            : slideAlign === "right"
            ? width * 0.92
            : width / 2;

        let textStartY = isTextBottom ? height * 0.81 : height * 0.06;
        let headlineTotalHeight = 0;

        if (slide.headline) {
          ctx.fillStyle = isLightColor(background) ? "#0F172A" : "#FFFFFF";
          ctx.font = `bold ${Math.round(width * 0.064)}px ${fontFamily}`;
          ctx.textAlign = slideAlign;
          ctx.textBaseline = "top";
          headlineTotalHeight = wrapCanvasText(
            ctx,
            slide.headline,
            textX,
            textStartY,
            maxTextW,
            headlineLineHeight
          );
        }

        if (slide.subhead) {
          ctx.fillStyle = isLightColor(background) ? "#475569" : "#94A3B8";
          ctx.font = `500 ${Math.round(width * 0.030)}px ${fontFamily}`;
          ctx.textAlign = slideAlign;
          ctx.textBaseline = "top";
          const subheadY = textStartY + headlineTotalHeight + height * 0.012;
          wrapCanvasText(
            ctx,
            slide.subhead,
            textX,
            subheadY,
            maxTextW,
            subheadLineHeight
          );
        }
      }

      // 2. Draw Device and Screenshot
      const screenshotImg = new Image();
      screenshotImg.crossOrigin = "anonymous";
      screenshotImg.onload = () => {
        const bezelImg = new Image();
        bezelImg.crossOrigin = "anonymous";
        bezelImg.onload = () => {
          ctx.save();

          // Device Dimensions based on Layout
          const phoneW = width * (layoutSpec.deviceWidthPct / 100);
          const aspect = activeFrame.geom.height / activeFrame.geom.width;
          const phoneH = phoneW * aspect;
          const phoneX = (width - phoneW) / 2 + (layoutSpec.deviceOffsetLeft / 100) * width;

          let phoneY = height * 0.17 + (layoutSpec.deviceOffsetTop / 100) * height;
          if (isTextBottom) {
            phoneY = height * 0.04 + (layoutSpec.deviceOffsetTop / 100) * height;
          } else if (isTextNone) {
            phoneY = (height - phoneH) / 2;
          }

          // Apply Rotation if specified
          if (layoutSpec.deviceRotate) {
            ctx.translate(phoneX + phoneW / 2, phoneY + phoneH / 2);
            ctx.rotate((layoutSpec.deviceRotate * Math.PI) / 180);
            ctx.translate(-(phoneX + phoneW / 2), -(phoneY + phoneH / 2));
          }

          // Screen Cutout Box Geometry (Seamless underlap eliminating any gap)
          const geom = activeFrame.geom;
          const screenX = phoneX + (geom.screen.x / geom.width) * phoneW;
          const screenY = phoneY + (geom.screen.y / geom.height) * phoneH;
          const screenW = (geom.screen.width / geom.width) * phoneW;
          const screenH = (geom.screen.height / geom.height) * phoneH;
          const screenRadius = (geom.radius / geom.width) * phoneW;

          if (screenOnly) {
            // Screen-Only Mode: Rounded screenshot with realistic drop shadow
            ctx.shadowColor = "rgba(0, 0, 0, 0.4)";
            ctx.shadowBlur = width * 0.06;
            ctx.shadowOffsetY = width * 0.03;

            ctx.save();
            ctx.beginPath();
            ctx.roundRect(screenX, screenY, screenW, screenH, screenRadius);
            ctx.clip();
            ctx.drawImage(screenshotImg, screenX, screenY, screenW, screenH);
            ctx.restore();
          } else {
            // Bezel Mode: Screenshot tucked underneath, bezel PNG on top!
            ctx.shadowColor = "rgba(0, 0, 0, 0.45)";
            ctx.shadowBlur = width * 0.07;
            ctx.shadowOffsetY = width * 0.035;

            // Fill black background under screen
            ctx.fillStyle = "#000000";
            ctx.beginPath();
            ctx.roundRect(screenX, screenY, screenW, screenH, screenRadius);
            ctx.fill();

            // Draw clipped screenshot
            ctx.shadowColor = "transparent";
            ctx.save();
            ctx.beginPath();
            ctx.roundRect(screenX, screenY, screenW, screenH, screenRadius);
            ctx.clip();
            ctx.drawImage(screenshotImg, screenX, screenY, screenW, screenH);
            ctx.restore();

            // Draw Bezel Image on Top
            ctx.drawImage(bezelImg, phoneX, phoneY, phoneW, phoneH);
          }

          ctx.restore();
          resolve(canvas);
        };
        bezelImg.src = activeFrame.image;
      };
      screenshotImg.src = slide.image;
    });
  };

  // Export all as ZIP
  const handleExportZip = async () => {
    if (slides.length === 0) return;
    setIsExporting(true);
    setExportStatus("Preparing ZIP archive...");
    const zip = new JSZip();

    try {
      for (let i = 0; i < slides.length; i++) {
        setExportStatus(`Rendering slide ${i + 1} of ${slides.length}...`);
        const canvas = await renderTileToCanvas(slides[i], i);
        const dataUrl = canvas.toDataURL("image/png", 1.0);
        const base64 = dataUrl.replace(/^data:image\/png;base64,/, "");
        zip.file(`SnapStudio_${activeTemplate.key}_${i + 1}.png`, base64, { base64: true });
      }

      setExportStatus("Packaging archive...");
      const blob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.download = `SnapStudio_Export_${Date.now()}.zip`;
      link.href = URL.createObjectURL(blob);
      link.click();
    } catch (err) {
      console.error("Export error", err);
    } finally {
      setIsExporting(false);
      setExportStatus("");
    }
  };

  // Active frame choices filtered by deviceType (iPhone or Android)
  const availableFrames = Object.entries(FRAME_META).filter(
    ([_, meta]) => meta.device === deviceType
  );

  const activeSlide = slides[activeSlideIndex] || null;

  return (
    <div className="goldie-studio-app">
      {/* Accessible Semantic Heading & SEO Content for Google Search Crawlers */}
      <div className="sr-only" aria-hidden="true">
        <h1>Free App Store & Google Play Store Screenshot Generator | SnapStudio</h1>
        <p>
          Design high-converting Apple App Store and Google Play Store screenshots online for free.
          Includes authentic iPhone 17 Pro and Android Google Pixel device frames, zero-gap screen
          cutouts, curated gradient backgrounds, customized typography, and high-resolution ZIP exports.
        </p>
      </div>

      {/* 1. Header Toolbar */}
      <header className="goldie-header">
        <div className="header-left">
          <Link to="/" className="back-btn" title="Back to Portfolio">
            <ArrowBack className="icon" />
            <span>Portfolio</span>
          </Link>
          <div className="v-divider" />
          <div className="brand">
            <span className="brand-goldie">SnapStudio</span>
          </div>
        </div>

        <div className="header-center">
          {/* Device Type Switcher Tabs (iPhone & Android Only) */}
          <div className="device-tabs">
            <button
              className={`dev-tab ${deviceType === "iphone" ? "active" : ""}`}
              onClick={() => handleDeviceChange("iphone")}
            >
              <Smartphone className="tab-icon" />
              <span>iPhone</span>
            </button>
            <button
              className={`dev-tab ${deviceType === "android" ? "active" : ""}`}
              onClick={() => handleDeviceChange("android")}
            >
              <Android className="tab-icon" />
              <span>Android</span>
            </button>
          </div>
        </div>

        <div className="header-right">
          <button
            className="btn-upload"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <CloudUpload className="btn-icon" />
            <span>Add Screenshots</span>
          </button>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            multiple
            onChange={(e) => handleFilesUpload(e.target.files)}
            style={{ display: "none" }}
          />

          <button
            className="btn-export-primary"
            onClick={handleExportZip}
            disabled={slides.length === 0 || isExporting}
          >
            <FolderZip className="btn-icon" />
            <span>{isExporting ? exportStatus || "Exporting..." : `Export (${slides.length})`}</span>
          </button>
        </div>
      </header>

      {/* 2. Main Studio Workspace */}
      <div className="goldie-workspace">
        {/* Left Stage Area with Zoom & Gesture */}
        <main
          ref={stageRef}
          className={`goldie-stage ${isDragging ? "dragging" : ""} ${isPanning ? "panning" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onMouseDown={handleMouseDownStage}
          onMouseMove={handleMouseMoveStage}
          onMouseUp={handleMouseUpStage}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {slides.length === 0 ? (
            /* EXACT GOLDIE EMPTY STATE (No Preloaded Demo) */
            <div className="goldie-empty-state">
              <div className="empty-box">
                <div className="empty-icon-circle">
                  <PhotoCamera className="icon" />
                </div>
                <h2 className="empty-title">No screenshots added yet</h2>
                <p className="empty-desc">
                  Drag and drop your raw mobile app screenshots here, or click the button below to
                  browse files.
                </p>

                <div className="empty-actions">
                  <button
                    className="empty-btn-upload"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    <CloudUpload className="btn-icon" />
                    <span>Upload Screenshots</span>
                  </button>

                  <button className="empty-btn-sample" onClick={handleLoadSampleStyles}>
                    <span>Or Explore with Sample Styles</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* GOLDIE TILE STRIP CAROUSEL (Zoomable with Gestures) */
            <div className="goldie-strip-container">
              <div className="strip-header-info">
                <span className="strip-count">
                  Showing <strong>{slides.length}</strong> {slides.length === 1 ? "screenshot" : "screenshots"} • Device: <strong>{deviceType === "android" ? "Android Pixel" : "iPhone 17 Pro"}</strong> • Template: <strong>{activeTemplate.label}</strong>
                </span>
                <div className="strip-header-actions">
                  <button className="btn-action-text" onClick={handlePopulateDummyText} title="Fill all screens with dummy titles and subtitles">
                    <AutoFixHigh className="icon" /> Fill Dummy Copy
                  </button>
                  <button className="btn-action-text" onClick={handleClearAllText} title="Remove all headlines & subtitles">
                    <DeleteSweep className="icon" /> Clear Copy
                  </button>
                  <button className="btn-clear-all" onClick={() => setSlides([])}>
                    Remove All
                  </button>
                </div>
              </div>

              {/* Zoom & Pan Viewport */}
              <div
                ref={viewportRef}
                className="strip-scroll-viewport"
                style={{
                  transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                  transformOrigin: "center center"
                }}
              >
                <div className="strip-scroll-track">
                  {slides.map((slide, idx) => {
                    const layoutKey = getSlideLayoutKey(slide, idx);
                    const layoutSpec = LAYOUT_DEFS[layoutKey] || LAYOUT_DEFS.classic;
                    const isTextNone = layoutSpec.copyPosition === "none";
                    const isTextBottom = layoutSpec.copyPosition === "bottom";
                    const slideAlign = slide.align || textAlign || layoutSpec.copyAlign || "center";

                    return (
                      <div
                        key={slide.id}
                        className={`strip-tile ${idx === activeSlideIndex ? "active" : ""} layout-${layoutKey}`}
                        onClick={() => setActiveSlideIndex(idx)}
                        style={{
                          background: background === TRANSPARENT ? CHECKERBOARD : background
                        }}
                      >
                        {/* Slide Layout Pill Indicator */}
                        <div className="tile-layout-pill" title={`Layout: ${layoutSpec.label}`}>
                          {layoutSpec.label}
                        </div>

                        {/* Delete tile button */}
                        <button
                          className="tile-delete-btn"
                          title="Delete screenshot"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSlides(slides.filter((_, sIdx) => sIdx !== idx));
                          }}
                        >
                          <Close className="icon" />
                        </button>

                        {/* In-place Editable Headline & Subhead (Auto-adjusting height, no awkward gaps) */}
                        {!isTextNone && (
                          <div
                            className={`tile-text-container ${isTextBottom ? "bottom" : "top"}`}
                            style={{
                              textAlign: slideAlign,
                              order: isTextBottom ? 2 : 1
                            }}
                          >
                            <div className="input-with-clear">
                              <textarea
                                ref={(el) => {
                                  if (el) {
                                    el.style.height = "auto";
                                    el.style.height = el.scrollHeight + "px";
                                  }
                                }}
                                rows={1}
                                className="tile-input-headline"
                                value={slide.headline || ""}
                                placeholder="Add Headline..."
                                style={{
                                  color: isLightColor(background) ? "#0F172A" : "#FFFFFF",
                                  fontFamily,
                                  textAlign: slideAlign
                                }}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  e.target.style.height = "auto";
                                  e.target.style.height = e.target.scrollHeight + "px";
                                  setSlides(
                                    slides.map((s, i) => (i === idx ? { ...s, headline: val } : s))
                                  );
                                }}
                              />
                              {slide.headline && (
                                <button
                                  className="btn-clear-field"
                                  title="Remove headline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSlides(
                                      slides.map((s, i) => (i === idx ? { ...s, headline: "" } : s))
                                    );
                                  }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>

                            <div className="input-with-clear">
                              <textarea
                                ref={(el) => {
                                  if (el) {
                                    el.style.height = "auto";
                                    el.style.height = el.scrollHeight + "px";
                                  }
                                }}
                                rows={1}
                                className="tile-input-subhead"
                                value={slide.subhead || ""}
                                placeholder="Add Subtitle..."
                                style={{
                                  color: isLightColor(background) ? "#475569" : "#94A3B8",
                                  fontFamily,
                                  textAlign: slideAlign
                                }}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  e.target.style.height = "auto";
                                  e.target.style.height = e.target.scrollHeight + "px";
                                  setSlides(
                                    slides.map((s, i) => (i === idx ? { ...s, subhead: val } : s))
                                  );
                                }}
                              />
                              {slide.subhead && (
                                <button
                                  className="btn-clear-field"
                                  title="Remove subtitle"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSlides(
                                      slides.map((s, i) => (i === idx ? { ...s, subhead: "" } : s))
                                    );
                                  }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Real Device Frame (Zero Gap Underlap with Bezel on Top) */}
                        <div
                          className={`tile-device-wrap ${layoutSpec.bleedBottom ? "bleed-bottom" : ""}`}
                          style={{
                            order: isTextBottom ? 1 : 2,
                            width: `${layoutSpec.deviceWidthPct}%`,
                            transform: `translate(${layoutSpec.deviceOffsetLeft}%, ${layoutSpec.deviceOffsetTop}%) rotate(${layoutSpec.deviceRotate}deg)`
                          }}
                        >
                          <div
                            className="device-frame-box"
                            style={{
                              aspectRatio: `${activeFrame.geom.width} / ${activeFrame.geom.height}`
                            }}
                          >
                            {/* Screen Cutout with Screenshot Underlap */}
                            <div
                              className="screen-cutout"
                              style={{
                                left: `${(activeFrame.geom.screen.x / activeFrame.geom.width) * 100}%`,
                                top: `${(activeFrame.geom.screen.y / activeFrame.geom.height) * 100}%`,
                                width: `${(activeFrame.geom.screen.width / activeFrame.geom.width) * 100}%`,
                                height: `${(activeFrame.geom.screen.height / activeFrame.geom.height) * 100}%`,
                                borderRadius: `${(activeFrame.geom.radius / activeFrame.geom.screen.width) * 100}% / ${(activeFrame.geom.radius / activeFrame.geom.screen.height) * 100}%`,
                                boxShadow: screenOnly
                                  ? "0 18px 45px rgba(0, 0, 0, 0.45)"
                                  : undefined
                              }}
                            >
                              <img src={slide.image} alt="Screenshot" draggable={false} />
                            </div>

                            {/* Official Frame Bezel PNG layered directly over screenshot */}
                            {!screenOnly && (
                              <img
                                src={activeFrame.image}
                                alt={activeFrame.label}
                                className="bezel-overlay-img"
                                draggable={false}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bottom Floating Zoom Controls Bar */}
              <div className="floating-zoom-bar">
                <button
                  className="btn-zoom-circle"
                  title="Zoom Out (Trackpad Pinch or Ctrl + Wheel)"
                  onClick={() => setZoomLevel((prev) => Math.max(0.35, Number((prev - 0.1).toFixed(2))))}
                >
                  <ZoomOut />
                </button>
                <button
                  className="zoom-text-btn"
                  title="Click to reset zoom to 100%"
                  onClick={handleResetView}
                >
                  {Math.round(zoomLevel * 100)}%
                </button>
                <button
                  className="btn-zoom-circle"
                  title="Zoom In (Trackpad Pinch or Ctrl + Wheel)"
                  onClick={() => setZoomLevel((prev) => Math.min(2.5, Number((prev + 0.1).toFixed(2))))}
                >
                  <ZoomIn />
                </button>
                <div className="bar-divider" />
                <button
                  className="btn-zoom-circle"
                  title="Fit All Screens"
                  onClick={handleFitToScreen}
                >
                  <FitScreen />
                </button>
                <button
                  className="btn-zoom-circle"
                  title="Reset View"
                  onClick={handleResetView}
                >
                  <RestartAlt />
                </button>
              </div>
            </div>
          )}
        </main>

        {/* Right Goldie Design Panel Sidebar */}
        <aside className="goldie-sidebar">
          <div className="sidebar-header">
            <span className="sidebar-title">Design</span>
          </div>

          <div className="sidebar-scroll">
            {/* 1. Background Section */}
            <div className="design-field">
              <div className="field-header">
                <label className="field-label">Background</label>
                <span className="field-hint">
                  {backgroundTab === "gradients" ? "Gradient" : "Solid Color"}
                </span>
              </div>

              {/* Tabs: Gradients vs Solid colors */}
              <div className="goldie-tabs">
                <button
                  className={`g-tab ${backgroundTab === "gradients" ? "active" : ""}`}
                  onClick={() => setBackgroundTab("gradients")}
                >
                  Gradients
                </button>
                <button
                  className={`g-tab ${backgroundTab === "solids" ? "active" : ""}`}
                  onClick={() => setBackgroundTab("solids")}
                >
                  Solid colors
                </button>
              </div>

              {/* Swatches Grid */}
              <div className="swatches-grid">
                {backgroundTab === "gradients"
                  ? GRADIENTS.map((p) => (
                      <button
                        key={p.name}
                        className={`swatch-btn ${background === p.css ? "selected" : ""}`}
                        style={{ background: p.css }}
                        title={p.name}
                        onClick={() => setBackground(p.css)}
                      >
                        {background === p.css && (
                          <Check className={`check-icon ${isLightColor(p.css) ? "dark" : "light"}`} />
                        )}
                      </button>
                    ))
                  : SOLIDS.map((p) => (
                      <button
                        key={p.name}
                        className={`swatch-btn ${background === p.css ? "selected" : ""}`}
                        style={{ background: p.css }}
                        title={p.name}
                        onClick={() => setBackground(p.css)}
                      >
                        {background === p.css && (
                          <Check className={`check-icon ${isLightColor(p.css) ? "dark" : "light"}`} />
                        )}
                      </button>
                    ))}

                {/* Transparent Swatch */}
                <button
                  className={`swatch-btn ${background === TRANSPARENT ? "selected" : ""}`}
                  style={{ background: CHECKERBOARD }}
                  title="Transparent"
                  onClick={() => setBackground(TRANSPARENT)}
                >
                  {background === TRANSPARENT && <Check className="check-icon dark" />}
                </button>

                {/* Goldie Conic Gradient Custom Color Picker Button (With Pencil) */}
                <button
                  className="swatch-btn custom-conic-btn"
                  title="Custom Color / Pencil Picker"
                  onClick={() => setCustomColorOpen(!customColorOpen)}
                >
                  <span className="conic-ring" />
                  <Edit className="pencil-icon" />
                </button>
              </div>

              {/* Custom Color Popover (From / To / Angle Slider) */}
              {customColorOpen && (
                <div className="custom-color-popover">
                  <div
                    className="custom-preview-bar"
                    style={{
                      background: `linear-gradient(${customAngle}deg, ${customFrom} 0%, ${customTo} 100%)`
                    }}
                  />

                  <div className="color-inputs-row">
                    <div className="color-field">
                      <label>From</label>
                      <div className="color-picker-wrap">
                        <input
                          type="color"
                          value={customFrom}
                          onChange={(e) => setCustomFrom(e.target.value)}
                        />
                        <span className="hex-val">{customFrom}</span>
                      </div>
                    </div>

                    <div className="color-field">
                      <label>To</label>
                      <div className="color-picker-wrap">
                        <input
                          type="color"
                          value={customTo}
                          onChange={(e) => setCustomTo(e.target.value)}
                        />
                        <span className="hex-val">{customTo}</span>
                      </div>
                    </div>
                  </div>

                  <div className="slider-row">
                    <div className="slider-header">
                      <span>Angle</span>
                      <span>{customAngle}°</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="360"
                      value={customAngle}
                      onChange={(e) => setCustomAngle(Number(e.target.value))}
                    />
                  </div>

                  <button className="btn-apply-color" onClick={applyCustomColor}>
                    Use this background
                  </button>
                </div>
              )}
            </div>

            {/* 2. Goldie Exact Template Picker (With Visual 5-Tile Strip Glyphs) */}
            <div className="design-field">
              <div className="field-header">
                <label className="field-label">Template Strip</label>
                <span className="field-hint">{activeTemplate.label}</span>
              </div>

              <div className="goldie-template-glyphs-list">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.key}
                    className={`template-glyph-card ${template === t.key ? "selected" : ""}`}
                    onClick={() => handleTemplateChange(t.key)}
                  >
                    <div className="glyph-preview-wrap">
                      <TemplateGlyphs sequence={t.sequence} />
                    </div>
                    <div className="glyph-card-footer">
                      <div className="glyph-info">
                        <span className="glyph-title">{t.label}</span>
                        <span className="glyph-desc">{t.desc}</span>
                      </div>
                      {template === t.key && <Check className="check-icon" />}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* 3. Typography & Text Options */}
            <div className="design-field">
              <div className="field-header">
                <label className="field-label">Typography & Copy</label>
                <span className="field-hint">Auto Dummy Included</span>
              </div>

              {/* Active Slide Text Inputs */}
              {activeSlide && (
                <div className="active-slide-copy-card">
                  <div className="copy-card-title">
                    <span>Active Slide {activeSlideIndex + 1} Copy</span>
                  </div>
                  <div className="copy-input-group">
                    <label>Headline (Title)</label>
                    <div className="copy-input-row">
                      <input
                        type="text"
                        value={activeSlide.headline || ""}
                        placeholder="e.g. Discover Fast & Seamless"
                        onChange={(e) => {
                          const val = e.target.value;
                          setSlides(
                            slides.map((s, i) =>
                              i === activeSlideIndex ? { ...s, headline: val } : s
                            )
                          );
                        }}
                      />
                      {activeSlide.headline && (
                        <button
                          className="btn-clear-mini"
                          onClick={() => {
                            setSlides(
                              slides.map((s, i) =>
                                i === activeSlideIndex ? { ...s, headline: "" } : s
                              )
                            );
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="copy-input-group">
                    <label>Subtitle</label>
                    <div className="copy-input-row">
                      <input
                        type="text"
                        value={activeSlide.subhead || ""}
                        placeholder="e.g. Verified profiles & instant matching."
                        onChange={(e) => {
                          const val = e.target.value;
                          setSlides(
                            slides.map((s, i) =>
                              i === activeSlideIndex ? { ...s, subhead: val } : s
                            )
                          );
                        }}
                      />
                      {activeSlide.subhead && (
                        <button
                          className="btn-clear-mini"
                          onClick={() => {
                            setSlides(
                              slides.map((s, i) =>
                                i === activeSlideIndex ? { ...s, subhead: "" } : s
                              )
                            );
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Font Typeface List */}
              <div className="font-selector-list">
                {FONT_OPTIONS.map((f) => (
                  <button
                    key={f.label}
                    className={`font-option-btn ${fontFamily === f.css ? "selected" : ""}`}
                    onClick={() => setFontFamily(f.css)}
                  >
                    <span className="font-specimen" style={{ fontFamily: f.css }}>
                      Aa
                    </span>
                    <span className="font-label" style={{ fontFamily: f.css }}>
                      {f.label}
                    </span>
                    {fontFamily === f.css && <Check className="check-icon" />}
                  </button>
                ))}
              </div>

              {/* Text Alignment Row */}
              <div className="text-align-row">
                <span className="sub-label">Alignment</span>
                <div className="align-btn-group">
                  <button
                    className={`align-btn ${textAlign === "left" ? "active" : ""}`}
                    onClick={() => handleAlignmentChange("left")}
                    title="Align Left"
                  >
                    <FormatAlignLeft />
                  </button>
                  <button
                    className={`align-btn ${textAlign === "center" ? "active" : ""}`}
                    onClick={() => handleAlignmentChange("center")}
                    title="Align Center"
                  >
                    <FormatAlignCenter />
                  </button>
                  <button
                    className={`align-btn ${textAlign === "right" ? "active" : ""}`}
                    onClick={() => handleAlignmentChange("right")}
                    title="Align Right"
                  >
                    <FormatAlignRight />
                  </button>
                </div>
              </div>
            </div>

            {/* 4. Frame Section (Bezel vs Screen only) */}
            <div className="design-field">
              <div className="field-header">
                <label className="field-label">Device Frame</label>
                <span className="field-hint">{activeFrame.label}</span>
              </div>

              {/* Bezel vs Screen only Tabs */}
              <div className="goldie-tabs">
                <button
                  className={`g-tab ${!screenOnly ? "active" : ""}`}
                  onClick={() => setScreenOnly(false)}
                >
                  Bezel Frame
                </button>
                <button
                  className={`g-tab ${screenOnly ? "active" : ""}`}
                  onClick={() => setScreenOnly(true)}
                >
                  Screen only
                </button>
              </div>

              {/* Bezel Tint Options with real device thumbnail */}
              {!screenOnly && (
                <div className="frame-options-list">
                  {availableFrames.map(([key, meta]) => (
                    <button
                      key={key}
                      className={`frame-option-btn ${frameKey === key ? "selected" : ""}`}
                      onClick={() => handleFrameChange(key)}
                    >
                      <span className="frame-swatch" style={{ background: meta.tint }} />
                      <div className="frame-info">
                        <span className="frame-name">{meta.label}</span>
                        <span className="frame-device-tag">{meta.device.toUpperCase()}</span>
                      </div>
                      {frameKey === key && <Check className="check-icon" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default SnapStudio;
